import { useEffect, useMemo } from 'react';
import { CarePlanCycleType, ClinicCarePlanFragment } from 'shared/types/graphql';
import { useManageMembershipContext } from '../components/ManageMembership/context/ManageMembershipContext';
import { PaymentsMade } from '../components/ManageMembership/types';
import { calcPaymentsMade, calcDifferenceAndFee } from '../utils/membershipCalculator';
import { centsToDollars } from '../utils/currency';
import { getBillsPerRenewal } from '../utils/planRenewalCycle';
import { useCareEnrollmentInfo } from './useCareEnrollmentInfo';

interface UseUpgradeCalculatorInput {
  enrollmentId: string;
  selectedPlanId: string;
  plans: ClinicCarePlanFragment[];
}

interface PlanInfo {
  id: string;
  title?: string;
  costPerBill: number;
  billingCycle?: CarePlanCycleType;
}

interface UseUpgradeCalculatorOutput {
  currentPlan: PlanInfo;
  selectedPlan: PlanInfo;
  paymentsMade: PaymentsMade;
  differenceBetweenPlans: number;
  upgradeFee: number;
  isLoading: boolean;
  hasDisputedPayments: boolean;
}

export const useUpgradeCalculator = ({
  enrollmentId,
  selectedPlanId,
  plans,
}: UseUpgradeCalculatorInput): UseUpgradeCalculatorOutput => {
  const { setBalanceDue } = useManageMembershipContext();

  const {
    plan: currentCarePlan,
    planPricing: currentCarePlanPricing,
    validPayments,
    disputedPaymentCount,
    isLoading,
  } = useCareEnrollmentInfo({
    enrollmentId,
  });

  const currentPlan: PlanInfo = useMemo(() => {
    return {
      id: currentCarePlan?.id || '',
      title: currentCarePlan?.flowAlias || currentCarePlan?.title,
      costPerBill: currentCarePlanPricing?.pricePerRenewal
        ? centsToDollars(currentCarePlanPricing?.pricePerRenewal) /
          getBillsPerRenewal(currentCarePlanPricing?.renewalCycle, currentCarePlanPricing?.billingCycle)
        : 0,
      billingCycle: currentCarePlanPricing?.billingCycle,
    };
  }, [currentCarePlan, currentCarePlanPricing]);

  const selectedPlan: PlanInfo = useMemo(() => {
    const plan = plans.find((plan) => plan.id === selectedPlanId);
    const planPricing = plan?.planPricings?.find(
      (pricing) => pricing.billingCycle === currentCarePlanPricing?.billingCycle && pricing.published,
    );
    return {
      id: plan?.id || '',
      title: plan?.flowAlias || plan?.title,
      costPerBill: planPricing?.pricePerRenewal
        ? centsToDollars(planPricing?.pricePerRenewal) /
          getBillsPerRenewal(planPricing?.renewalCycle, planPricing?.billingCycle)
        : 0,
      billingCycle: planPricing?.billingCycle,
    };
  }, [plans, selectedPlanId, currentCarePlanPricing?.billingCycle]);

  const paymentsMade: PaymentsMade = useMemo(() => {
    return calcPaymentsMade({
      payments: validPayments,
      pricePerRenewal: currentCarePlanPricing?.pricePerRenewal,
      renewalCycle: currentCarePlanPricing?.renewalCycle,
      billingCycle: currentCarePlanPricing?.billingCycle,
    });
  }, [
    currentCarePlanPricing?.pricePerRenewal,
    currentCarePlanPricing?.renewalCycle,
    validPayments,
    currentCarePlanPricing?.billingCycle,
  ]);

  const { differenceBetweenPlans, upgradeFee } = useMemo(() => {
    return calcDifferenceAndFee({
      currentPlanCostPerBill: currentPlan.costPerBill,
      selectedPlanCostPerBill: selectedPlan.costPerBill,
      paymentsMade: paymentsMade.quantity,
    });
  }, [currentPlan.costPerBill, paymentsMade.quantity, selectedPlan.costPerBill]);

  useEffect(() => {
    setBalanceDue(upgradeFee >= 0 ? upgradeFee : undefined);
  }, [setBalanceDue, upgradeFee]);

  return {
    currentPlan,
    selectedPlan,
    differenceBetweenPlans,
    upgradeFee,
    paymentsMade,
    isLoading,
    hasDisputedPayments: disputedPaymentCount > 0,
  };
};
